/**
 * Domain Types: Teams & Collaboration
 *
 * Multi-user collaboration system for brands/teams.
 * Enables multiple users to work on the same business/brand.
 *
 * Key Features:
 * - Brand/team management
 * - Role-based access control (RBAC)
 * - User invitations
 * - Activity tracking
 * - Shared resources (services, pricing, bookings)
 */

// ============================================================================
// Team/Brand Entity
// ============================================================================

export interface Team {
  // Identity
  id: string;
  name: string;
  slug: string;  // URL-friendly identifier

  // Ownership
  ownerId: string;
  ownerEmail: string;

  // Branding
  logo: string | null;
  description: string | null;
  website: string | null;

  // Settings
  settings: TeamSettings;

  // Metadata
  createdAt: string;
  updatedAt: string;

  // Soft delete
  deletedAt: string | null;
  deletedBy: string | null;
}

export interface TeamSettings {
  // Collaboration
  allowMemberInvites: boolean;
  requireApprovalForInvites: boolean;

  // Resources
  sharedServices: boolean;
  sharedPricing: boolean;
  sharedClients: boolean;

  // Notifications
  notifyOnNewBooking: boolean;
  notifyOnPriceChange: boolean;
  notifyOnMemberJoin: boolean;
}

// ============================================================================
// Team Member Entity
// ============================================================================

export interface TeamMember {
  // Identity
  id: string;
  teamId: string;
  userId: string;
  userEmail: string;

  // Role
  role: TeamRole;

  // Status
  status: MemberStatus;
  invitedBy: string | null;
  invitedAt: string;
  joinedAt: string | null;

  // Permissions (override defaults)
  customPermissions: Partial<RolePermissions> | null;

  // Metadata
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Roles & Permissions
// ============================================================================

export type TeamRole = 'owner' | 'admin' | 'editor' | 'viewer';

export type MemberStatus = 'invited' | 'active' | 'suspended' | 'left';

export interface RolePermissions {
  // Services
  viewServices: boolean;
  createServices: boolean;
  editServices: boolean;
  deleteServices: boolean;
  archiveServices: boolean;

  // Pricing
  viewPricing: boolean;
  editPricing: boolean;
  lockPricing: boolean;

  // Bookings
  viewBookings: boolean;
  createBookings: boolean;
  editBookings: boolean;
  cancelBookings: boolean;

  // Clients
  viewClients: boolean;
  createClients: boolean;
  editClients: boolean;
  deleteClients: boolean;

  // Snapshots
  viewSnapshots: boolean;
  createSnapshots: boolean;
  lockSnapshots: boolean;

  // Financial
  viewFinancials: boolean;
  editFinancials: boolean;

  // Team Management
  viewMembers: boolean;
  inviteMembers: boolean;
  removeMembers: boolean;
  editMemberRoles: boolean;

  // Settings
  viewSettings: boolean;
  editSettings: boolean;
}

// Default permissions for each role
export const ROLE_PERMISSIONS: Record<TeamRole, RolePermissions> = {
  owner: {
    viewServices: true,
    createServices: true,
    editServices: true,
    deleteServices: true,
    archiveServices: true,
    viewPricing: true,
    editPricing: true,
    lockPricing: true,
    viewBookings: true,
    createBookings: true,
    editBookings: true,
    cancelBookings: true,
    viewClients: true,
    createClients: true,
    editClients: true,
    deleteClients: true,
    viewSnapshots: true,
    createSnapshots: true,
    lockSnapshots: true,
    viewFinancials: true,
    editFinancials: true,
    viewMembers: true,
    inviteMembers: true,
    removeMembers: true,
    editMemberRoles: true,
    viewSettings: true,
    editSettings: true
  },
  admin: {
    viewServices: true,
    createServices: true,
    editServices: true,
    deleteServices: true,
    archiveServices: true,
    viewPricing: true,
    editPricing: true,
    lockPricing: true,
    viewBookings: true,
    createBookings: true,
    editBookings: true,
    cancelBookings: true,
    viewClients: true,
    createClients: true,
    editClients: true,
    deleteClients: false,
    viewSnapshots: true,
    createSnapshots: true,
    lockSnapshots: true,
    viewFinancials: true,
    editFinancials: true,
    viewMembers: true,
    inviteMembers: true,
    removeMembers: true,
    editMemberRoles: false,  // Can't change roles (owner only)
    viewSettings: true,
    editSettings: false       // Can't change settings (owner only)
  },
  editor: {
    viewServices: true,
    createServices: true,
    editServices: true,
    deleteServices: false,
    archiveServices: true,
    viewPricing: true,
    editPricing: true,
    lockPricing: false,       // Can't lock pricing
    viewBookings: true,
    createBookings: true,
    editBookings: true,
    cancelBookings: false,
    viewClients: true,
    createClients: true,
    editClients: true,
    deleteClients: false,
    viewSnapshots: true,
    createSnapshots: true,
    lockSnapshots: false,     // Can't lock snapshots
    viewFinancials: true,
    editFinancials: false,    // Can't edit financials
    viewMembers: true,
    inviteMembers: false,
    removeMembers: false,
    editMemberRoles: false,
    viewSettings: true,
    editSettings: false
  },
  viewer: {
    viewServices: true,
    createServices: false,
    editServices: false,
    deleteServices: false,
    archiveServices: false,
    viewPricing: true,
    editPricing: false,
    lockPricing: false,
    viewBookings: true,
    createBookings: false,
    editBookings: false,
    cancelBookings: false,
    viewClients: true,
    createClients: false,
    editClients: false,
    deleteClients: false,
    viewSnapshots: true,
    createSnapshots: false,
    lockSnapshots: false,
    viewFinancials: true,
    editFinancials: false,
    viewMembers: true,
    inviteMembers: false,
    removeMembers: false,
    editMemberRoles: false,
    viewSettings: true,
    editSettings: false
  }
};

// ============================================================================
// Team Invitation
// ============================================================================

export interface TeamInvitation {
  id: string;
  teamId: string;
  teamName: string;

  // Invitee
  email: string;
  role: TeamRole;

  // Invitation
  invitedBy: string;
  invitedByName: string;
  invitedAt: string;
  expiresAt: string;

  // Token
  token: string;

  // Status
  status: InvitationStatus;
  acceptedAt: string | null;
  rejectedAt: string | null;

  // Message
  personalMessage: string | null;
}

export type InvitationStatus = 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled';

// ============================================================================
// Activity Log
// ============================================================================

export interface TeamActivity {
  id: string;
  teamId: string;

  // Actor
  actorId: string;
  actorName: string;
  actorRole: TeamRole;

  // Action
  action: ActivityAction;
  resourceType: ResourceType;
  resourceId: string | null;
  resourceName: string | null;

  // Details
  details: ActivityDetails;

  // Metadata
  createdAt: string;
  ipAddress: string | null;
  userAgent: string | null;
}

export type ActivityAction =
  // Services
  | 'service_created'
  | 'service_updated'
  | 'service_deleted'
  | 'service_archived'
  | 'service_restored'
  // Pricing
  | 'pricing_updated'
  | 'pricing_locked'
  // Bookings
  | 'booking_created'
  | 'booking_updated'
  | 'booking_cancelled'
  // Snapshots
  | 'snapshot_created'
  | 'snapshot_locked'
  // Team
  | 'member_invited'
  | 'member_joined'
  | 'member_removed'
  | 'member_role_changed'
  | 'settings_updated';

export type ResourceType =
  | 'service'
  | 'shoot_day'
  | 'booking'
  | 'snapshot'
  | 'client'
  | 'expense'
  | 'goal'
  | 'member'
  | 'settings';

export interface ActivityDetails {
  changes?: Array<{
    field: string;
    oldValue: unknown;
    newValue: unknown;
  }>;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// Helper Types
// ============================================================================

export interface CreateTeamInput {
  name: string;
  slug?: string;
  description?: string;
  website?: string;
  settings?: Partial<TeamSettings>;
}

export interface UpdateTeamInput {
  name?: string;
  slug?: string;
  logo?: string;
  description?: string;
  website?: string;
  settings?: Partial<TeamSettings>;
}

export interface InviteMemberInput {
  teamId: string;
  email: string;
  role: TeamRole;
  personalMessage?: string;
}

export interface UpdateMemberRoleInput {
  memberId: string;
  role: TeamRole;
  customPermissions?: Partial<RolePermissions>;
}

export interface TeamContext {
  team: Team;
  member: TeamMember;
  permissions: RolePermissions;
}
